import { SvgIconComponent } from "@mui/icons-material";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import {
    Button,
    ButtonGroup,
    ButtonGroupProps,
    ButtonProps,
    Divider,
    Menu,
    MenuItem,
    MenuProps,
    Typography,
    useTheme
} from "@mui/material";
import { Theme } from '@mui/material/styles';
import { noop } from "lodash";
import React, {
    FC,
    HTMLAttributeAnchorTarget,
    MouseEventHandler,
    ReactElement,
    useState,
} from "react";

interface ISlotProps {
    buttonGroup?: ButtonGroupProps;
    button?: ButtonProps;
    dropdownButton?: ButtonProps;
    menu?: MenuProps;
}

interface IDropdownItem {
    content: string | number | ReactElement;
    onClick?: MouseEventHandler;
    href?: string;
    showInButton?: boolean;
    disableMenuItemWrapper?: boolean;
    shouldCloseMenuOnClick?: boolean;
    target?: HTMLAttributeAnchorTarget;
}

interface IwButtonDropdownPropsInterface {
    dropdownItems: IDropdownItem[];
    slotProps?: ISlotProps;
    ExpandIcon?: SvgIconComponent;
    shape?: "square" | "rounded";
}

const MENU_STYLES =(theme: Theme)=> ( {
    // as interview details modal that does pop up while uploading audio
    // has zIndex 52
    zIndex: 52,
    ".MuiMenuItem-root": {
        fontSize: "14px",
        fontWeight: 400,
        padding: "8px 16px",
        ":hover": {
            backgroundColor: theme?.custom?.hoverOptionColor,
        },
    },
    ".MuiSvgIcon-root": {
        fontSize: "20px",
    },
});
const MAIN_BUTTON_STYLES = (theme: Theme)=> ( {
    textTransform: "capitalize",
    fontSize: "14px",
    marginRight: "-1px",
    borderColor: theme?.custom?.borderGrey,
    color: theme?.custom?.mainIconColor,
    whiteSpace: "nowrap",
    fontWeight: 400,
    ":hover": {
        color: theme?.custom?.mainButtonHighlight,
    },
});
const BUTTON_DIVIDER_STYLES = (theme: Theme)=> ( {
    borderColor: theme?.custom?.borderGrey,
    height: "20px",
    alignSelf: "center",
});
const DROPDOWN_BUTTON_STYLES = (theme: Theme)=> ( {
    padding: 0,
    borderColor: theme?.custom?.borderGrey,
    borderLeftColor: "transparent",
    color: theme?.custom?.mainIconColor,
    ":hover": {
        color: theme?.custom?.mainButtonHighlight,
    },
});
const MENU_PAPER_STYLES = {
    marginTop: "4px",
    minWidth: "190px",
};

const filterDropdownItems = (dropdownItems: IDropdownItem[]) => {
    if (!dropdownItems || dropdownItems.length === 0) {
        return {
            itemToShowInButton: null,
            itemsToShowInDropdown: [],
        };
    }
    let itemsToShowInDropdown: IDropdownItem[] = [...dropdownItems];
    let itemToShowInButton: IDropdownItem = null;
    let priorityItemIndex = dropdownItems.findIndex((item) => item?.showInButton);

    priorityItemIndex = priorityItemIndex !== -1 ? priorityItemIndex : 0;
    itemToShowInButton = dropdownItems[priorityItemIndex];

    itemsToShowInDropdown.splice(priorityItemIndex, 1);

    return {
        itemToShowInButton,
        itemsToShowInDropdown,
    };
};

const IwButtonDropdown: FC<IwButtonDropdownPropsInterface> = (props) => {
    const { slotProps, ExpandIcon, dropdownItems, shape } = props;
    const {
        buttonGroup: buttonGroupProps,
        button: buttonProps,
        dropdownButton: dropdownButtonProps,
        menu: menuProps,
    } = slotProps;
    const {
        open: openMenuFromProps,
        onClose: onCloseMenu,
        sx: menuSxFromProps,
        ...otherMenuProps
    } = menuProps || {};
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>(null);
    const muiTheme = useTheme();
    const { itemToShowInButton, itemsToShowInDropdown } =
        filterDropdownItems(dropdownItems);
    const shouldOpenMenu =
        openMenuFromProps !== undefined ? openMenuFromProps : !!anchorEl;
    const buttonBorderRadius =
        shape === "square" ? `${muiTheme.shape.borderRadius.toFixed()}px` : "100px";
    const theme = useTheme();
    const handleMenuOpen = (anchor: HTMLButtonElement) => {
        setAnchorEl(anchor);
    };

    const handleMenuClose = (
        event: React.MouseEvent<
            HTMLButtonElement | HTMLLIElement | HTMLAnchorElement
        >,
        reason?
    ) => {
        setAnchorEl(null);

        if (!!onCloseMenu) {
            onCloseMenu(event, reason);
        }
    };

    const handleExpandButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        if (!anchorEl) {
            handleMenuOpen(event.currentTarget);
        } else {
            handleMenuClose(event);
        }
    };

    const onMenuItemClick = (
        event: React.MouseEvent<
            HTMLButtonElement | HTMLLIElement | HTMLAnchorElement
        >,
        onClick: MouseEventHandler = noop,
        shouldCloseMenuOnClick?: boolean
    ) => {
        onClick(event);

        if (shouldCloseMenuOnClick) {
            handleMenuClose(event);
        }
    };

    return (
        <>
            <ButtonGroup
                sx={{
                    ".MuiButtonGroup-grouped": {
                        minWidth: "34px",
                    },
                }}
                {...buttonGroupProps}
            >
                <Button
                    size="small"
                    onClick={itemToShowInButton.onClick}
                    sx={{
                        borderRadius: buttonBorderRadius,
                        ...MAIN_BUTTON_STYLES(theme),
                    }}
                    {...buttonProps}
                >
                    {itemToShowInButton.content}
                </Button>
                <Divider
                    orientation="vertical"
                    flexItem={true}
                    sx={BUTTON_DIVIDER_STYLES(theme)}
                />
                <Button
                    size="small"
                    onClick={handleExpandButtonClick}
                    sx={{
                        borderRadius: buttonBorderRadius,
                        ...DROPDOWN_BUTTON_STYLES(theme),
                    }}
                    {...dropdownButtonProps}
                >
                    <ExpandIcon />
                </Button>
            </ButtonGroup>
            <Menu
                open={shouldOpenMenu}
                anchorEl={anchorEl}
                onClose={handleMenuClose}
                sx={{
                    ...MENU_STYLES(theme),
                    ...(!!menuSxFromProps ? menuSxFromProps : {}),
                }}
                slotProps={{
                    paper: {
                        sx: MENU_PAPER_STYLES,
                    },
                }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                {...otherMenuProps}
            >
                {itemsToShowInDropdown?.map((item,index) => {
                    return item?.disableMenuItemWrapper ? (
                        <Typography key={index} component="span">
                           {item?.content}
                        </Typography>
                    ) : (
                        <MenuItem
                            key={index}
                            target={item?.target}
                            component={!!item?.href ? "a" : "li"}
                            href={item?.href}
                            onClick={(event) => {
                                onMenuItemClick(
                                    event,
                                    item?.onClick,
                                    item?.shouldCloseMenuOnClick
                                );
                            }}
                        >
                            {item?.content}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

IwButtonDropdown.defaultProps = {
    slotProps: {
        buttonGroup: {},
        button: {},
        menu: {
            open: undefined,
        },
    },
    ExpandIcon: ExpandMoreOutlinedIcon,
    shape: "square",
};

export default IwButtonDropdown;
